import type {
  Circuit,
  CircuitCellStyle,
  CircuitEdge,
  CircuitEdgeColor,
  CircuitSurface,
} from '../types/track.js';
import { cellCoordKey, getCorePath } from './circuitEdit.js';
import { listValidCellStyleKeys } from './circuitDecorSurfaceEdit.js';

export type { CircuitSurface, CircuitEdge, CircuitEdgeColor, CircuitCellStyle };

export type SolidCircuitSurface = Exclude<CircuitSurface, 'damier'>;

export const SURFACE_FILL_CSS: Record<SolidCircuitSurface, string> = {
  asphalt: 'rgba(51, 65, 85, 0.94)',
  earth: 'rgba(146, 64, 14, 0.9)',
};

export const SURFACE_FILL_HEX: Record<SolidCircuitSurface, number> = {
  asphalt: 0x334155,
  earth: 0x92400e,
};

export const DAMIER_LIGHT_CSS = '#ffffff';
export const DAMIER_DARK_CSS = '#000000';
export const DAMIER_LIGHT_HEX = 0xffffff;
export const DAMIER_DARK_HEX = 0x000000;

export function isDamierSurface(surface: CircuitSurface): boolean {
  return surface === 'damier';
}

export function isSolidCircuitSurface(surface: CircuitSurface): surface is SolidCircuitSurface {
  return surface !== 'damier';
}

/** Damier noir et blanc sur une case (4×4 carreaux). */
export function drawDamierCellFill(
  fillRect: (x: number, y: number, w: number, h: number, color: string | number) => void,
  px: number,
  py: number,
  size: number,
  gridX: number,
  gridY: number,
  options?: { useHex?: boolean; tiles?: number }
): void {
  const tiles = options?.tiles ?? 4;
  const tile = size / tiles;
  for (let i = 0; i < tiles; i++) {
    for (let j = 0; j < tiles; j++) {
      const light = (gridX + i + gridY + j) % 2 === 0;
      const color = options?.useHex
        ? light
          ? DAMIER_LIGHT_HEX
          : DAMIER_DARK_HEX
        : light
          ? DAMIER_LIGHT_CSS
          : DAMIER_DARK_CSS;
      fillRect(px + i * tile, py + j * tile, tile, tile, color);
    }
  }
}

export const EDGE_STRIPE_CSS: Record<'white' | 'red', string> = {
  white: '#f8fafc',
  red: '#dc2626',
};

export const EDGE_STRIPE_HEX: Record<'white' | 'red', number> = {
  white: 0xf8fafc,
  red: 0xdc2626,
};

const VALID_EDGE_COLORS = new Set<CircuitEdgeColor>(['white', 'red', 'curb']);

export function isCircuitEdgeColor(value: unknown): value is CircuitEdgeColor {
  return typeof value === 'string' && VALID_EDGE_COLORS.has(value as CircuitEdgeColor);
}

export function getCircuitCellStyle(
  circuit: Circuit,
  x: number,
  y: number
): CircuitCellStyle | undefined {
  return circuit.cellStyles?.[cellCoordKey(x, y)];
}

export function isStyleableCircuitCell(circuit: Circuit, x: number, y: number): boolean {
  if (findPathIndexAtCoord(circuit, x, y) !== null) return true;
  if (circuit.jokerLap?.cells.some((c) => c.x === x && c.y === y)) return true;
  if (circuit.startLane?.cells.some((c) => c.x === x && c.y === y)) return true;
  return false;
}

function findPathIndexAtCoord(circuit: Circuit, x: number, y: number): number | null {
  const core = getCorePath(circuit.path);
  for (let i = 0; i < core.length; i++) {
    if (core[i].x === x && core[i].y === y) return i;
  }
  return null;
}

function sanitizeCellStyle(style: CircuitCellStyle): CircuitCellStyle | null {
  const next: CircuitCellStyle = {};
  if (style.fill) next.fill = style.fill;
  if (style.edges) {
    const edges: Partial<Record<CircuitEdge, CircuitEdgeColor>> = {};
    for (const [edge, color] of Object.entries(style.edges) as [CircuitEdge, unknown][]) {
      if (isCircuitEdgeColor(color)) edges[edge] = color;
    }
    if (Object.keys(edges).length > 0) next.edges = edges;
  }
  if (!next.fill && !next.edges) return null;
  return next;
}

export function listStyleableCellKeys(circuit: Circuit): Set<string> {
  const keys = new Set<string>();
  for (const cell of getCorePath(circuit.path)) {
    keys.add(cellCoordKey(cell.x, cell.y));
  }
  for (const cell of circuit.jokerLap?.cells ?? []) {
    keys.add(cellCoordKey(cell.x, cell.y));
  }
  for (const cell of circuit.startLane?.cells ?? []) {
    keys.add(cellCoordKey(cell.x, cell.y));
  }
  return keys;
}

export function pruneCellStyles(circuit: Circuit): Circuit {
  const styles = circuit.cellStyles;
  if (!styles) return circuit;
  const valid = listValidCellStyleKeys(circuit);
  const next: Record<string, CircuitCellStyle> = {};
  for (const [key, style] of Object.entries(styles)) {
    if (!valid.has(key)) continue;
    const sanitized = sanitizeCellStyle(style);
    if (sanitized) next[key] = sanitized;
  }
  return {
    ...circuit,
    cellStyles: Object.keys(next).length > 0 ? next : undefined,
  };
}

function upsertCellStyle(
  circuit: Circuit,
  x: number,
  y: number,
  update: (prev: CircuitCellStyle) => CircuitCellStyle | null
): Circuit {
  if (!isStyleableCircuitCell(circuit, x, y)) return circuit;
  const key = cellCoordKey(x, y);
  const styles = { ...(circuit.cellStyles ?? {}) };
  const prev = styles[key] ?? {};
  const nextStyle = update({ ...prev, edges: prev.edges ? { ...prev.edges } : undefined });
  if (!nextStyle) {
    delete styles[key];
  } else {
    styles[key] = nextStyle;
  }
  return {
    ...circuit,
    cellStyles: Object.keys(styles).length > 0 ? styles : undefined,
  };
}

export function setCircuitCellFill(
  circuit: Circuit,
  x: number,
  y: number,
  surface: CircuitSurface | null
): Circuit {
  return upsertCellStyle(circuit, x, y, (prev) => {
    const next: CircuitCellStyle = { ...prev, edges: prev.edges ? { ...prev.edges } : undefined };
    if (surface === null) {
      delete next.fill;
    } else {
      next.fill = surface;
    }
    if (!next.fill && (!next.edges || Object.keys(next.edges).length === 0)) return null;
    return next;
  });
}

export function setCircuitCellEdge(
  circuit: Circuit,
  x: number,
  y: number,
  edge: CircuitEdge,
  color: CircuitEdgeColor | null
): Circuit {
  return upsertCellStyle(circuit, x, y, (prev) => {
    const edges = { ...prev.edges };
    if (color === null) {
      delete edges[edge];
    } else {
      edges[edge] = color;
    }
    const next: CircuitCellStyle = { ...prev };
    if (Object.keys(edges).length > 0) {
      next.edges = edges;
    } else {
      delete next.edges;
    }
    if (!next.fill && !next.edges) return null;
    return next;
  });
}

/** Bord le plus proche du clic dans une case (coordonnées locales 0…cellSize). */
export function pickCircuitEdgeAtLocalPoint(
  localX: number,
  localY: number,
  cellSize: number
): CircuitEdge {
  const dNorth = localY;
  const dSouth = cellSize - localY;
  const dWest = localX;
  const dEast = cellSize - localX;
  const min = Math.min(dNorth, dSouth, dWest, dEast);
  if (min === dNorth) return 'north';
  if (min === dSouth) return 'south';
  if (min === dWest) return 'west';
  return 'east';
}

/** Segmente un bord pour le motif rouge / blanc alterné. */
export function forEachStyledEdgePart(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  style: CircuitEdgeColor,
  segmentLength: number,
  fn: (sx: number, sy: number, ex: number, ey: number, stripe: 'white' | 'red') => void
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return;
  const ux = dx / len;
  const uy = dy / len;

  if (style === 'white' || style === 'red') {
    fn(x1, y1, x2, y2, style);
    return;
  }

  const seg = Math.max(2, segmentLength);
  let pos = 0;
  let index = 0;
  while (pos < len - 1e-6) {
    const partLen = Math.min(seg, len - pos);
    const stripe: 'white' | 'red' = index % 2 === 0 ? 'red' : 'white';
    const sx = x1 + ux * pos;
    const sy = y1 + uy * pos;
    const ex = x1 + ux * (pos + partLen);
    const ey = y1 + uy * (pos + partLen);
    fn(sx, sy, ex, ey, stripe);
    pos += partLen;
    index += 1;
  }
}
